import type { Ref } from 'vue';

export type MaveVideoAutoplay = 'always' | 'lazy' | 'true';

export type MaveVideoPredefinedControls = 'full' | 'big' | 'none';

/**
 * Preset control bar (`full`, `big`, `none`) or space-separated control names,
 * e.g. `play time seek volume fullscreen`.
 */
export type MaveVideoControls = MaveVideoPredefinedControls | (string & {});

/** Mave `subtitles` attribute: language codes, `all`, or `original`. */
export type MaveVideoSubtitles = 'all' | 'original' | (string & {});

/** Control bar with playback time and optional subtitle toggle when subtitles are enabled. */
export const MAVE_VIDEO_CONTROLS_WITH_TIME_AND_SUBTITLES =
  'play time seek volume fullscreen subtitles';

export type MaveVideoProps = {
  videoId: string;
  /** When true, autoplays muted (required for browser autoplay policies). */
  autoplayMuted?: boolean;
  autoplay?: MaveVideoAutoplay | false;
  /**
   * `big` shows only a large play/pause button.
   * `full` shows the control bar including volume/mute.
   */
  controls?: MaveVideoControls;
  /** Mave `subtitles` attribute — e.g. `all`, `original`, or `en,de`. */
  subtitles?: MaveVideoSubtitles;
  /** Mave `active-subtitle` attribute — language code or `original`. */
  activeSubtitle?: string;
  /** Reduces on-screen subtitle size below the mobile layout breakpoint. */
  compactSubtitlesOnMobile?: boolean;
};

/** Exposed instance of the underlying Mave `Player` Vue wrapper. */
export type MavePlayerComponentInstance = {
  element: HTMLElement;
  paused: boolean;
  muted: boolean;
  play: () => void;
  pause: () => void;
};

export type MaveVideoExposed = {
  refreshRunningState: () => void;
  isVideoRunning: Ref<boolean>;
  hasUserEnabledSound: Ref<boolean>;
};
